import * as _ from 'lodash';
import { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import { GetFacilitiesTableLimitedDataQueryGQLType } from '../../../../../types/dashboard-core-gqlTypes.generated.d';
import { FacilitiesTableRowData } from '../facilitiesTableOptions';
import {
  GetFacilitiesTableDataQueryGQLType,
  useGetFacilitiesTableLimitedDataQuery,
} from '../getFacilitiesTableData.gql.generated';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { CreditFacility } from '~/contexts/CreditFacilitiesProvider/useCreditFacilities';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { computeTotalsRow } from '~/core/routes/credit-facilities/hooks/useFacilitiesTableData';
import { getDefaultDashboardDisplayStartDate } from '~/packages/common-frontend/constants';
import { useCheckIfAnyFacilitiesHaveBorrowingBaseQuery } from '~/packages/credit-facility/requests/requests.gql.generated';

const mapQueryDataToTableRows = ({
  creditFacilities,
  facilitiesTableQueryData: {
    slsCreditAgreementsPropertiesForCapitalProvider,
    fundingRequests,
    expensesFeesLedgerForAllCountersigners,
    outstandingLedgerForAllCountersigners,
    outstandingLedgerForCapitalProvider,
  },
}: {
  creditFacilities: CreditFacility[];
  facilitiesTableQueryData: GetFacilitiesTableLimitedDataQueryGQLType;
}): FacilitiesTableRowData[] => {
  const creditAgreementPropertiesMap = new Map(
    slsCreditAgreementsPropertiesForCapitalProvider.map((obj) => [
      obj.creditFacilityId,
      obj,
    ]),
  );
  // Bucket data from all of the different queries by credit facility
  const creditFacilitiesWithData = creditFacilities.map((creditFacility) => {
    return {
      creditFacility,
      lastFundingRequest: _.orderBy(
        fundingRequests.nodes.filter(
          (obj) => obj.creditFacilityId === creditFacility.id,
        ),
        'created_at',
        'desc',
      )[0] as
        | GetFacilitiesTableDataQueryGQLType['fundingRequests']['nodes'][number]
        | undefined,
      expensesFeesLedgerForFacilityForAllCountersigners: expensesFeesLedgerForAllCountersigners.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      outstandingLedgerForFacilityForAllCountersigners: outstandingLedgerForAllCountersigners.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
      outstandingLedgerForFacilityForCapitalProvider: outstandingLedgerForCapitalProvider.find(
        (obj) => obj.creditFacilityId === creditFacility.id,
      ),
    };
  });

  const creditFacilityRows: (FacilitiesTableRowData | null)[] = creditFacilitiesWithData.map(
    ({
      creditFacility,
      lastFundingRequest,
      expensesFeesLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForAllCountersigners,
      outstandingLedgerForFacilityForCapitalProvider,
    }) => {
      const creditAgreementProperties = creditAgreementPropertiesMap.get(
        creditFacility.id,
      );

      if (!creditAgreementProperties) {
        return null;
      }

      const {
        externalFacilityId,
        maturityDate,
        closingDate,
        revolvingPeriodEndDate,
        globalCommitmentAmount,
        capitalProviderCommitmentAmount,
        interestRatesDisplay,
        unusedFeesDisplay,
        upfrontFeesDisplay,
      } = creditAgreementProperties;

      const globalLoanOutstanding =
        outstandingLedgerForFacilityForAllCountersigners?.ledgerBalance ?? 0;

      return {
        // "Facility"
        creditFacilityId: creditFacility.id,
        creditFacilityName: creditFacility.name,
        externalFacilityId: externalFacilityId ?? null,
        maturityDate,
        closingDate,
        revolvingPeriodEndDate: revolvingPeriodEndDate ?? undefined,

        // "Global Commitment Amount"
        globalCommitmentAmount: globalCommitmentAmount ?? 0,
        // "Global Outstanding Balance"
        globalLoanOutstanding,
        // "Global Unused Commitment"
        globalUnusedCommitment:
          globalCommitmentAmount -
          (outstandingLedgerForFacilityForAllCountersigners?.ledgerBalance ??
            0),
        // "Global Utilization"
        globalUtilization:
          globalCommitmentAmount > 0
            ? globalLoanOutstanding / globalCommitmentAmount
            : 0,

        // Capital provider-only cells. These are hidden for borrowers.
        ...(() => {
          const capitalProviderLoanOutstanding =
            outstandingLedgerForFacilityForCapitalProvider?.ledgerBalance ?? 0;
          const capitalProviderUnused =
            capitalProviderCommitmentAmount - capitalProviderLoanOutstanding;

          return {
            // "Capital Provider Commitment Amount"
            capitalProviderCommitmentAmount,
            // "Capital Provider Outstanding Balance"
            capitalProviderLoanOutstanding,
            // "Capital Provider Unused Commitment"
            capitalProviderUnused,
            // "Capital Provider Utilization"
            capitalProviderUtilization:
              capitalProviderCommitmentAmount > 0
                ? capitalProviderLoanOutstanding /
                  capitalProviderCommitmentAmount
                : 0,
          };
        })(),

        accruedInterestAndFees:
          expensesFeesLedgerForFacilityForAllCountersigners?.ledgerBalance ?? 0,
        // "Interest Rates"
        interestRates: interestRatesDisplay,
        unusedFees: unusedFeesDisplay ?? '-',
        upfrontFees: upfrontFeesDisplay ?? '-',

        // "Date of Last Funding Request"
        dateOfLastFundingRequest: lastFundingRequest
          ? commonDate.epochMsToFullDateStr(lastFundingRequest.created_at)
          : undefined,

        // null properties
        collectionsAccountBalance: 0,
        borrowingBaseFromReceivables: 0,
        receivableAdvanceRate: null,
        borrowingBaseCashContribution: 0,
        requiredReserve: 0,
        totalBorrowingBase: 0,
        borrowingBaseAvailability: 0,
        financialCovenantsFailed: null,
        portfolioCovenantsFailed: null,
      };
    },
  );
  return creditFacilityRows.filter((row) => row !== null);
};

// Exports
export const useFacilitiesTableDataSls = () => {
  const flags = useFeatureFlags();
  const { user } = useUserAuthOrThrow();
  const organizationId = user.organizationId;

  // Get credit facilities for this org
  const { facilities: creditFacilities } = useCreditFacilitiesOrThrow();
  const creditFacilityIds = creditFacilities.map(({ id }) => id);

  // Get the credit facility details that we can query in aggregate
  // TODO @sachinr4 - look into consolidating accounting calculations and aggregations used in this query (eg. outstanding balance)
  const facilitiesTableQuery = useGetFacilitiesTableLimitedDataQuery({
    // No credit facility IDs, most likely waiting on the credit facility query
    skip: creditFacilityIds.length === 0 || !flags.isSlsAgentLender,
    fetchPolicy: 'cache-and-network',
    variables: {
      creditFacilityIds,
      capitalProviderOrganizationId: organizationId,
      startDate: commonDate.fullDateStrToEpoch(
        getDefaultDashboardDisplayStartDate(),
      ),
    },
  });

  const checkHasBorrowingBaseQuery = useCheckIfAnyFacilitiesHaveBorrowingBaseQuery(
    {
      skip: creditFacilityIds.length === 0 || !flags.isSlsAgentLender,
      variables: {
        creditFacilityIds,
        creditFacilitySlugsToHide: flags.creditFacilitySlugsToHide ?? [],
      },
    },
  );
  const hasBorrowingBase =
    checkHasBorrowingBaseQuery.data?.hasBorrowingBase ?? false;

  const { computedRows, totalsRow } = useMemo(() => {
    if (!flags.isSlsAgentLender)
      return {
        computedRows: [],
        totalsRow: computeTotalsRow([], []),
        av: [],
      };
    const computed = facilitiesTableQuery.data
      ? mapQueryDataToTableRows({
          creditFacilities,
          facilitiesTableQueryData: facilitiesTableQuery.data,
        })
      : [];

    return {
      computedRows: computed,
      totalsRow: computeTotalsRow(computed, []),
    };
  }, [creditFacilities, facilitiesTableQuery.data, flags.isSlsAgentLender]);

  return {
    hasBorrowingBase,
    computedRows,
    totalsRow,
    balanceSheetImpactCalcs: null,
    attributeValues: [],
    attributeFiltersOptions: [],
    error: facilitiesTableQuery.error || checkHasBorrowingBaseQuery.error,
    isAnyLoading:
      facilitiesTableQuery.loading ||
      !facilitiesTableQuery.data ||
      checkHasBorrowingBaseQuery.loading ||
      !checkHasBorrowingBaseQuery.data,
    isHasBorrowingBaseLoading:
      checkHasBorrowingBaseQuery.loading || !checkHasBorrowingBaseQuery.data,
    isFacilitiesTableDataLoading:
      facilitiesTableQuery.loading || !facilitiesTableQuery.data,
    isBalanceSheetExposureLoading: false,
  };
};
